import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Shield, Lock, ArrowLeft, Check, AlertCircle } from "lucide-react";
import axios from "axios";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ceva nu a funcționat. Vă rugăm să încercați din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Token de resetare lipsă sau invalid.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Parolele introduse nu coincid.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        { token, password }
      );
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la resetarea parolei.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      data-testid="reset-password-container"
      className="min-h-screen bg-[#0A0A0A] flex flex-col justify-center items-center px-4 py-12 relative overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full filter blur-3xl -z-10" />

      <div className="w-full max-w-md bg-[#121212] border border-[#D4AF37]/20 p-8 sm:p-10 relative">
        <div className="absolute top-0 left-0 w-full h-[4px] bg-[#D4AF37]" />

        {/* Header */}
        <div className="text-center mb-8 space-y-2">
          <div className="mx-auto w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] border border-[#D4AF37]/20 mb-3">
            <Lock size={24} />
          </div>
          <h2 className="font-serif-lux text-2xl sm:text-3xl text-white font-medium">
            Setați Noua Parolă
          </h2>
          <p className="text-xs text-white/50 tracking-wider uppercase">
            Elysium Events Mansion
          </p>
        </div>

        {error && (
          <div 
            data-testid="reset-error-alert"
            className="mb-6 p-4 bg-red-950/40 border border-red-900/30 text-red-400 text-sm font-light flex items-start space-x-2"
          >
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div 
            data-testid="reset-success-view"
            className="text-center space-y-4 py-4"
          >
            <div className="mx-auto w-12 h-12 bg-green-950/40 text-green-400 flex items-center justify-center rounded-full border border-green-900/30">
              <Check size={24} />
            </div>
            <p className="text-sm text-white/80">
              Parola dumneavoastră a fost resetată cu succes! Vă puteți conecta acum în siguranță.
            </p>
            <Link
              to="/login"
              data-testid="reset-login-redirect-link"
              className="inline-block bg-[#D4AF37] text-black text-xs font-semibold tracking-widest uppercase px-6 py-3 transition-colors"
            >
              Autentificare
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Noua Parolă</label>
              <input
                type="password"
                required
                data-testid="reset-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Confirmă Noua Parolă</label>
              <input
                type="password"
                required
                data-testid="reset-password-confirm-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-button"
              className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-4 transition-all duration-300"
            >
              {loading ? "Se salvează..." : "Salvează Noua Parolă"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
